import React, {MouseEvent, ReactElement, useEffect, useState} from "react";
import {Checkbox} from "components/ui/checkbox";
import humanFormat from "human-format";
import {ChevronDownIcon, ChevronRightIcon} from "@radix-ui/react-icons";
import {ArrowRightFromLine, ArrowUpDown, SearchIcon, XIcon} from "lucide-react";
import {Input} from "components/ui/input";
import {Attribute} from "types/telemetry";
import {cn} from "../ui/lib/utils";
import {Tooltip, TooltipTrigger} from "../ui/tooltip";
import {TooltipContent} from "@radix-ui/react-tooltip";
import {LoadingSpinner} from "../ui/customSpinner";
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuTrigger,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";

// Define sort types enum
export enum FilterSortType {
    VOLUME = "volume",
    ALPHABETICAL = "alphabetical",
    REVERSE_ALPHABETICAL = "reverse_alphabetical"
}

// First, create a context to share sort type
const FilterPanelContext = React.createContext<{sortType: FilterSortType}>({sortType: FilterSortType.VOLUME});

export function FilterPanel(props: {
    filter: Map<string, string[]>,
    setFilter: (filter: Map<string, string[]>) => void,
    excludeFilter: Map<string, string[]>
    setExcludeFilter: (filter: Map<string, string[]>) => void
    attributes: Map<string, Attribute[]>,
    telemetryFiltersComponent: ReactElement,
    filteringCriteria: string,
    setFilterShrank?: (val: boolean) => void,
    initiallyOpenFilterKeys?: string[]
    requestRefresh?: React.Dispatch<React.SetStateAction<Map<string, boolean>>>
    setIsOpened?: React.Dispatch<React.SetStateAction<Map<string, boolean>>>
}) {
    const [filterFilter, setFilterFilter] = useState<string>("");
    const [shrinkFilter, setShrinkFilter] = useState<boolean>(false);
    const [sortType, setSortType] = useState<FilterSortType>(FilterSortType.VOLUME);

    let updateShrinkFilters = (shrank: boolean) => {
        if (props.setFilterShrank !== undefined) {
            props.setFilterShrank(shrank);
        }
        setShrinkFilter(shrank);
    };

    if (shrinkFilter) {
        return <div className={"w-[22px] flex hover:text-primary text-textlight"}>
            <ArrowRightFromLine
                className={"absolute top-0 right-0 z-20 w-5 h-5 hover:cursor-pointer hover:text-primary"}
                onClick={() => updateShrinkFilters(false)}
            />
            <div style={{writingMode: "vertical-lr", transform: "rotate(180deg)"}}
                 onClick={() => updateShrinkFilters(false)}
                 className={"text-textlight bg-backgroundmedium text-center border rounded grow shrink hover:bg-primarytransparent hover:cursor-pointer hover:text-primary hover:border-primaryhover"}
            >
                Show Filters
            </div>
        </div>
    }

    return (
        <FilterPanelContext.Provider value={{sortType}}>
            <div className={"w-[304px] bg-backgroundmedium flex-none select-none flex flex-col rounded"}>
                <Header setFilterFilter={setFilterFilter} setShrinkFilter={updateShrinkFilters} sortType={sortType} setSortType={setSortType}/>
                <div className={"h-full min-h-0 overflow-y-auto scrollMedium border-l grow border-b border-r rounded-b"}>
                    {props.telemetryFiltersComponent}
                    <CustomFilters
                        initiallyOpenFilterKeys={props.initiallyOpenFilterKeys}
                        filterFilter={filterFilter} filter={props.filter} setFilter={props.setFilter}
                        excludeFilter={props.excludeFilter} setExcludeFilter={props.setExcludeFilter}
                        attributes={props.attributes} filteringCriteria={props.filteringCriteria}
                        requestRefresh={props.requestRefresh}
                        setIsOpened={props.setIsOpened}
                        sortType={sortType}
                    />
                </div>
            </div>
        </FilterPanelContext.Provider>
    );
}

function Header(props: { 
    setFilterFilter: (filter: string) => void, 
    setShrinkFilter?: (val: boolean) => void,
    sortType: FilterSortType,
    setSortType: (sortType: FilterSortType) => void
}) {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    return <div className={"flex-none rounded-t h-[51px] w-full border border-border"}>
        <div className={"flex justify-between items-center p-2 h-full"}>
            {!isOpen ?
                <div onClick={() => setIsOpen(true)}
                     className={"w-6 h-6 relative text-textmedium hover:text-textlight hover:cursor-pointer"}>
                    <SearchIcon/>
                </div> : <SearchFilter setFilterFilter={props.setFilterFilter}/>}
            {
                !isOpen && <>
                    <div className={"font-normal text-textmedium text-xl"}>Filter</div>
                    <SortFilterMenu sortType={props.sortType} setSortType={props.setSortType} setShrinkFilter={props.setShrinkFilter} />
                </>
            }
            {isOpen &&
                <div onClick={() => {
                    setIsOpen(false);
                    props.setFilterFilter("");
                }}
                     className={"w-6 h-6 relative text-textmedium hover:bg-primary rounded hover:cursor-pointer"}>
                    <XIcon/>
                </div>
            }
        </div>
    </div>;
}

function SortFilterMenu(props: { 
    sortType: FilterSortType, 
    setSortType: (sortType: FilterSortType) => void,
    setShrinkFilter?: (val: boolean) => void
}) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <ArrowUpDown className="h-6 w-6 text-textmedium hover:text-primary" />
            </DropdownMenuTrigger>
            <DropdownMenuContent side={"right"} align="start" sideOffset={9} alignOffset={-14} className={"bg-background rounded text-textmedium border"}>
                <DropdownMenuRadioGroup 
                    value={props.sortType} 
                    onValueChange={(value) => props.setSortType(value as FilterSortType)}
                >
                    <DropdownMenuRadioItem value={FilterSortType.VOLUME} className={"hover:bg-backgroundlight"}>
                        Sort by volume
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value={FilterSortType.ALPHABETICAL} className={"hover:bg-backgroundlight"}>
                        Sort alphabetically
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value={FilterSortType.REVERSE_ALPHABETICAL} className={"hover:bg-backgroundlight"}>
                        Sort reverse alphabetically
                    </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
                <DropdownMenuSeparator className={"bg-border"} />
                <DropdownMenuItem 
                    className={"hover:bg-backgroundlight"}
                    onClick={() => {
                        if (props.setShrinkFilter) props.setShrinkFilter(true);
                    }}
                >
                    Hide Filters
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function SearchFilter(props: { setFilterFilter: (filter: string) => void }) {
    return <Input placeholder={"Search for filter..."} id={"search_filter_input"} onChangeCapture={(e) => {
        props.setFilterFilter(e.currentTarget.value);
    }} className={"h-[32px] rounded border-border w-10/12"}/>
}

export function TelemetryLevelFilters(props: {
    filter: Map<string, string[]>,
    attributes: Map<string, Attribute[]>,
    setFilter: (filter: Map<string, string[]>) => void
    excludeFilter: Map<string, string[]>,
    setExcludeFilter: (filter: Map<string, string[]>) => void
    levelAttributeKey: string
    indicatorColourFunction: (value: string) => string
}) {
    // Get the sortType from the FilterPanel context
    const { sortType } = React.useContext(FilterPanelContext);
    
    const statusCodes = props.attributes.get(props.levelAttributeKey);
    if (!statusCodes) {
        return null;
    }

    // Sort status codes based on the selected sort type
    let sortedStatusCodes = [...statusCodes];
    
    if (sortType === FilterSortType.ALPHABETICAL) {
        sortedStatusCodes.sort((a, b) => a.value.localeCompare(b.value));
    } else if (sortType === FilterSortType.REVERSE_ALPHABETICAL) {
        sortedStatusCodes.sort((a, b) => b.value.localeCompare(a.value));
    } else {
        // Default sort by volume
        sortedStatusCodes.sort((a, b) => b.volume - a.volume);
    }

    const statusCodeFilters: ReactElement[] = [];
    for (const statusCode of sortedStatusCodes) {
        const levelName = statusCode.value;
        const volume = statusCode.volume;
        statusCodeFilters.push(<FilterSelector filterFilter={""}
                                               indicatorColor={props.indicatorColourFunction(statusCode.value)}
                                               key={levelName} filterKey={props.levelAttributeKey}
                                               filterValue={levelName}
                                               volume={volume} filter={props.filter} setFilter={props.setFilter}
                                               excludeFilter={props.excludeFilter}
                                               setExcludeFilter={props.setExcludeFilter}
        />)
    }

    return <div className={"py-2"}>
        {statusCodeFilters}
    </div>
}

export function CustomFilters(props: {
    filterFilter: string,
    filter: Map<string, string[]>,
    excludeFilter: Map<string, string[]>,
    attributes: Map<string, Attribute[]>,
    setFilter: (filter: Map<string, string[]>) => void,
    setExcludeFilter: (filter: Map<string, string[]>) => void,
    filteringCriteria: string
    initiallyOpenFilterKeys?: string[]
    requestRefresh?: React.Dispatch<React.SetStateAction<Map<string, boolean>>>
    setIsOpened?: React.Dispatch<React.SetStateAction<Map<string, boolean>>>
    sortType?: FilterSortType
}) {
    const filters = Array.from(props.attributes.keys()).filter((key) => key !== props.filteringCriteria);
    filters.sort((a, b) => {
        if (a === "server.service.name") return -1;
        if (b === "server.service.name") return 1;
        if (a === "client.service.name") return -1;
        if (b === "client.service.name") return 1;
        if (a === "service.name") return -1;
        if (b === "service.name") return 1;
        if (a == "ServiceName") return -1;
        if (b == "ServiceName") return 1;
        return 0;
    });
    const customFilters: ReactElement[] = [];
    for (const filter of filters) {
        const filterValues = props.attributes.get(filter);
        if (filterValues) {
            customFilters.push(<FilterGroup
                requestRefresh={props.requestRefresh}
                setIsOpened={props.setIsOpened}
                initiallyOpenFilterKeys={props.initiallyOpenFilterKeys}
                filterFilter={props.filterFilter} key={filter} filterKey={filter}
                filterValues={filterValues}
                filter={props.filter} setFilter={props.setFilter}
                excludeFilter={props.excludeFilter}
                setExcludeFilter={props.setExcludeFilter}
                sortType={props.sortType}
            />)
        }
    }
    return <div>
        {customFilters}
    </div>
}

function FilterGroup(props: {
    filterFilter: string,
    filter: Map<string, string[]>,
    filterKey: string,
    filterValues: Attribute[],
    setFilter: (filter: Map<string, string[]>) => void
    excludeFilter: Map<string, string[]>,
    setExcludeFilter: (filter: Map<string, string[]>) => void
    initiallyOpenFilterKeys?: string[]
    requestRefresh?: React.Dispatch<React.SetStateAction<Map<string, boolean>>>
    setIsOpened?: React.Dispatch<React.SetStateAction<Map<string, boolean>>>
    sortType?: FilterSortType
}) {
    // These are the filter keys that should be open by default
    const [isOpen, setIsOpen] = useState<boolean>((props.initiallyOpenFilterKeys || []).includes(props.filterKey));
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        if (props.setIsOpened) {
            props.setIsOpened(prev => {
                let map = new Map(prev);
                map.set(props.filterKey, isOpen);
                return map;
            });
        }
    }, [isOpen]);

    let filters: ReactElement[] = [];
    let hasMatchingFilters = false;
    
    // Sort filter values based on the selected sort type
    let sortedFilterValues = [...props.filterValues];
    
    if (props.sortType === FilterSortType.ALPHABETICAL) {
        // Special case for service name-related filters - sort by the formatted name
        if (props.filterKey === "service.name" || props.filterKey === "service_name" || props.filterKey === "ServiceName" ||
            props.filterKey === "client.service.name" || props.filterKey === "server.service.name" || props.filterKey === "peer.service.name") {
            sortedFilterValues.sort((a, b) => 
                formatServiceNameFilterValues(a.value).localeCompare(formatServiceNameFilterValues(b.value))
            );
        } 
        // Special case for container ID-related filters
        else if (props.filterKey === "container.id" || props.filterKey === "container_id" ||
                props.filterKey === "server.container.id" || props.filterKey === "client.container.id") {
            sortedFilterValues.sort((a, b) => 
                formatContainerIdFilterValues(a.value).localeCompare(formatContainerIdFilterValues(b.value))
            );
        }
        else {
            sortedFilterValues.sort((a, b) => a.value.localeCompare(b.value));
        }
    } else if (props.sortType === FilterSortType.REVERSE_ALPHABETICAL) {
        // Special case for service name-related filters - sort by the formatted name
        if (props.filterKey === "service.name" || props.filterKey === "service_name" || props.filterKey === "ServiceName" ||
            props.filterKey === "client.service.name" || props.filterKey === "server.service.name" || props.filterKey === "peer.service.name") {
            sortedFilterValues.sort((a, b) => 
                formatServiceNameFilterValues(b.value).localeCompare(formatServiceNameFilterValues(a.value))
            );
        } 
        // Special case for container ID-related filters
        else if (props.filterKey === "container.id" || props.filterKey === "container_id" ||
                props.filterKey === "server.container.id" || props.filterKey === "client.container.id") {
            sortedFilterValues.sort((a, b) => 
                formatContainerIdFilterValues(b.value).localeCompare(formatContainerIdFilterValues(a.value))
            );
        }
        else {
            sortedFilterValues.sort((a, b) => b.value.localeCompare(a.value));
        }
    } else {
        // Default sort by volume (no change needed since that's the default order)
        sortedFilterValues.sort((a, b) => b.volume - a.volume);
    }
    
    for (const filterValue of sortedFilterValues) {
        if (props.filterFilter !== "" && !filterValue.value.includes(props.filterFilter) && !props.filterKey.includes(props.filterFilter)) {
            continue;
        }
        hasMatchingFilters = true;
        filters.push(<FilterSelector filterFilter={props.filterFilter} key={filterValue.value}
                                     filterKey={props.filterKey} filterValue={filterValue.value}
                                     filterValueDisplay={formatFilterValues(props.filterKey, filterValue.value)}
                                     volume={filterValue.volume} filter={props.filter} setFilter={props.setFilter}
                                     excludeFilter={props.excludeFilter} setExcludeFilter={props.setExcludeFilter}/>)
    }
    filters = filters.filter((filter) => filter !== null);
    let open = isOpen || (props.filterFilter !== "" && hasMatchingFilters);
    
    return <div className={cn("group border rounded mx-2 mb-2", !open ? "hover:bg-primary" : "")}>
        <FilterHeader filterFilter={props.filterFilter} isOpen={open} setIsOpen={(val) => {
            if (val && !isOpen) {
                setIsLoading(true);
                props.requestRefresh?.(prev => {
                    let map = new Map(prev);
                    map.set(props.filterKey, true);
                    return map;
                });
                // Set loading to false after a short delay to allow for data refresh
                setTimeout(() => setIsLoading(false), 1000);
            }
            setIsOpen(val);
        }} name={props.filterKey}/>
        {open && <div className={"py-2"}>
            {isLoading ? (
                <div className={"flex justify-center"}><LoadingSpinner/></div>
            ) : props.filterValues.length === 0 ? (
                <div className={"flex justify-center text-textmedium"}>No filters</div>
            ) : filters.length > 0 ? (
                filters
            ) : (
                <div className={"flex justify-center text-textmedium"}>No matching filters</div>
            )}
        </div>}
        {!open && <div className={""}></div>}
    </div>
}

export function FilterSelector(props: {
    filterValueDisplay?: string,
    filterFilter: string,
    filter: Map<string, string[]>,
    indicatorColor?: string,
    filterKey: string,
    filterValue: string,
    volume: number,
    setFilter: (filter: Map<string, string[]>) => void
    excludeFilter: Map<string, string[]>,
    setExcludeFilter: (filter: Map<string, string[]>) => void
}) {
    let checked = false;
    if (!props.filter.has(props.filterKey)) {
        checked = true;
    }
    if (props.filter.has(props.filterKey) && props.filter.get(props.filterKey)?.includes(props.filterValue)) {
        checked = true;
    }
    if (props.excludeFilter.has(props.filterKey) && props.excludeFilter.get(props.filterKey)?.includes(props.filterValue)) {
        checked = false;
    }
    const updateChecked = (checked: boolean) => {
        const newExcludeFilter = new Map(props.excludeFilter);
        // If it isnt checked, then we should remove it from the exclude filter if it exists, otherwise we need to add it to the inclusive filter
        if (checked) {
            // If the filter is already in the exclude filter, we should remove it
            if (newExcludeFilter.has(props.filterKey) && newExcludeFilter.get(props.filterKey)?.includes(props.filterValue)) {
                // Remove the value from the exclude filter
                const newValues = newExcludeFilter.get(props.filterKey)?.filter((value) => value !== props.filterValue);
                if (newValues && newValues.length > 0) {
                    newExcludeFilter.set(props.filterKey, newValues);
                } else {
                    newExcludeFilter.delete(props.filterKey);
                }
                props.setExcludeFilter(newExcludeFilter);
                return;
            }
            // Otherwise it means that there is another inclusive filter for the key so we should add this value to the inclusive filter
            const newFilter = new Map(props.filter);
            if (newFilter.has(props.filterKey)) {
                newFilter.get(props.filterKey)?.push(props.filterValue);
                props.setFilter(newFilter);
                return;
            }
        } else {
            // We must add it to the exclude filter
            if (!newExcludeFilter.has(props.filterKey)) {
                newExcludeFilter.set(props.filterKey, [props.filterValue]);
                props.setExcludeFilter(newExcludeFilter);
                return;
            } else {
                newExcludeFilter.get(props.filterKey)?.push(props.filterValue);
                props.setExcludeFilter(newExcludeFilter);
                return;
            }
        }
    }
    const all = props.filter.has(props.filterKey) && props.filter.get(props.filterKey)?.length == 1 && props.filter.get(props.filterKey)?.includes(props.filterValue);

    const updateOverallClicked = (e: MouseEvent<HTMLDivElement>) => {
        if (all) {
            const newExcludeFilter = new Map(props.excludeFilter);
            newExcludeFilter.delete(props.filterKey);
            props.setExcludeFilter(newExcludeFilter);
            const newFilter = new Map(props.filter);
            newFilter.delete(props.filterKey);
            props.setFilter(newFilter);
            return;
        } else {
            const newExcludeFilter = new Map(props.excludeFilter);
            newExcludeFilter.delete(props.filterKey);
            props.setExcludeFilter(newExcludeFilter);
            const newFilter = new Map(props.filter);
            newFilter.set(props.filterKey, [props.filterValue]);
            props.setFilter(newFilter);
            return;
        }
    }

    return <div id={"toplevel"} onClick={(e) => updateOverallClicked(e)}
                className={"min-w-0 overflow-hidden group/item flex justify-between p-1 px-4 items-center hover:cursor-pointer hover:bg-primarytransparent border-primary hover:p-[3px] hover:px-[15px] hover:border border-opacity-0 hover:border-opacity-100"}>
        <div className={"flex justify-start space-x-2 items-center"}>
            <Checkbox onClick={(e) => {
                e.stopPropagation()
            }} checked={checked} onCheckedChange={updateChecked}/>
            {
                props.indicatorColor &&
                <div className={"mt-[1px] w-0.5 h-3.5 rounded " + props.indicatorColor}/>
            }
            <Tooltip delayDuration={30}>
                <TooltipTrigger>
                    {
                        <div
                            dangerouslySetInnerHTML={{__html: props.filterFilter !== "" ? (props.filterValueDisplay ? props.filterValueDisplay : props.filterValue).replace(props.filterFilter, '<mark class="highlight">$&</mark>') : (props.filterValueDisplay ? props.filterValueDisplay : props.filterValue)}}
                            className={"mt-[1px] text-left truncate w-[140px] text-textmedium text-sm font-medium font-['Inter'] leading-[16px]"}>
                        </div>
                    }
                </TooltipTrigger>
                <TooltipContent side={"right"}
                                className={"ml-[100px] border bg-backgroundmedium text-textmedium p-1 rounded z-50"}>
                    {props.filterValueDisplay ? props.filterValueDisplay : props.filterValue}
                </TooltipContent>
            </Tooltip>

        </div>
        <div className={"flex mt-[1px] justify-end"}>
            <div className={"flex"}>
                <div
                    className={"hidden group-hover/item:block text-textlight bold text-sm font-bold leading-[16px] mr-[-4px]"}>
                    {
                        all ? "All" : "Only"
                    }
                </div>
                <div
                    className={"text-textmedium text-sm font-medium font-['Inter'] leading-[16px] group-hover/item:hidden"}>{props.volume !== 0 ? humanFormat(props.volume, {maxDecimals: 1}) : "-"}</div>
            </div>
        </div>
    </div>
}


function FilterHeader(props: {
    name: string,
    isOpen: boolean,
    setIsOpen: (isOpen: boolean) => void,
    filterFilter: string
}) {
    return <div className={""}>
        <div onClick={() => props.setIsOpen(!props.isOpen)}
             className={cn("hover:cursor-pointer hover:bg-primary h-6 border-b bg-backgroundlight rounded-t justify-start items-center px-2 gap-2 inline-flex w-full", !props.isOpen && props.filterFilter === "" ? "border-b-0 group-hover:bg-primary" : "")}>
            <div className="stroke-textmedium">
                {
                    props.isOpen ?
                        <ChevronDownIcon/> :
                        <ChevronRightIcon/>
                }</div>
            <Tooltip delayDuration={30}>
                <TooltipTrigger className={"text-textmedium text-sm font-semibold leading-tight truncate"}>
                    <div
                        dangerouslySetInnerHTML={{__html: props.filterFilter !== "" ? props.name.replace(props.filterFilter, '<mark class="highlight">$&</mark>') : props.name}}
                        className="text-textmedium text-sm font-semibold leading-tight truncate"></div>
                </TooltipTrigger>
                <TooltipContent
                    className={"border bg-backgroundmedium text-textmedium p-1 rounded z-50"}>{props.name}</TooltipContent>
            </Tooltip>

        </div>
    </div>
}

export function formatFilterValues(filterKey: string, filterValue: string) {
    if (filterKey === "server.service.name") {
        return formatServiceNameFilterValues(filterValue);
    }
    if (filterKey === "client.service.name") {
        return formatServiceNameFilterValues(filterValue);
    }
    if (filterKey === "service.name" || filterKey === "service_name" || filterKey === "ServiceName") {
        return formatServiceNameFilterValues(filterValue);
    }
    if (filterKey === "peer.service.name") {
        return formatServiceNameFilterValues(filterValue);
    }
    if (filterKey === "client.name") {
        return formatServiceNameFilterValues(filterValue);
    }
    if (filterKey === "container.id" || filterKey === "container_id") {
        return formatContainerIdFilterValues(filterValue);
    }
    if (filterKey === "server.container.id") {
        return formatContainerIdFilterValues(filterValue);
    }
    if (filterKey === "client.container.id") {
        return formatContainerIdFilterValues(filterValue);
    }
    return filterValue;
}

export function formatServiceNameFilterValues(filterValue: string) {
// Split the path by slashes
    const segments = filterValue.split('/');
// Return the last segment
    return segments[segments.length - 1];
}

export function formatContainerIdFilterValues(filterValue: string): string {
// Split the path by slashes
    const segments = filterValue.split('/');
// Get the last two segments
    return segments.slice(-2).join('/');
}


export function RemoveK8s(filterValue: string) {
    const prefix = "/k8s/";
    if (filterValue.startsWith(prefix)) {
        return filterValue.slice(prefix.length);
    }
    return filterValue;
}
